import { type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/error-boundary';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import NotFound from '@/pages/not-found';
import Home from '@/pages/Home';
import Styles from '@/pages/Styles';
import About from '@/pages/About';
import Upload from '@/pages/Upload';
import Pricing from '@/pages/Pricing';
import Looks from '@/pages/Looks';
import {
  AccountDeletionPage,
  PrivacyPolicy,
  SubscriptionTerms,
  SupportPage,
  TermsOfService,
} from '@/pages/Legal';
import { Shell } from '@/components/Shell';
import {
  Route,
  Switch,
  useLocation,
  Router as WouterRouter,
} from 'wouter';

const queryClient = new QueryClient();

function Router() {
  return (
    // Keep a shared shell (sidebar, navbar) outside the boundary so it
    // survives a page crash.
    <RoutedErrorBoundary>
      <Shell>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/styles" component={Styles} />
          <Route path="/about" component={About} />
          <Route path="/try" component={Upload} />
          <Route path="/pricing" component={Pricing} />
          <Route path="/looks" component={Looks} />
          <Route path="/privacy" component={PrivacyPolicy} />
          <Route path="/terms" component={TermsOfService} />
          <Route path="/support" component={SupportPage} />
          <Route path="/delete-account" component={AccountDeletionPage} />
          <Route path="/subscription-terms" component={SubscriptionTerms} />
          <Route component={NotFound} />
        </Switch>
      </Shell>
    </RoutedErrorBoundary>
  );
}

function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  return <ErrorBoundary resetKey={location}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
          <Router />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
